import { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useNavigate, useParams } from "react-router-dom";

import {
  HiOutlineArrowLeft,
  HiOutlineMagnifyingGlass,
  HiOutlineUserGroup,
  HiOutlineEnvelope,
  HiOutlineShieldCheck,
  HiOutlineClock,
  HiOutlineCalendarDays,
} from "react-icons/hi2";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageCard from "../../../components/ui/pageCard/PageCard";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";
import PageEmpty from "../../../components/ui/pageEmpty/PageEmpty";

import {
  getOrganizationById,
  getOrganizationCoaches,
} from "../../../redux/features/organizations/organizationSlice";

import "./OrganizationCoachesPage.css";

const ROLE_LABELS = {
  POWER_INTERNAL_COACH: "Interne coach",
  POWER_EXTERNAL_COACH: "Externe coach",
  POWER_ORGANIZATION_ADMIN: "Organisatie admin",
};

const STATUS_LABELS = {
  ACTIVE: "Actief",
  INVITED: "Uitgenodigd",
  BLOCKED: "Geblokkeerd",
  DISABLED: "Uitgeschakeld",
};

export default function OrganizationCoachesPage() {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { id } = useParams();

  const [search, setSearch] = useState("");

  const [statusFilter, setStatusFilter] = useState("");

  const {
    selectedOrganization,

    organizationCoaches,

    organizationCoachesPagination,

    isLoading,

    isLoadingCoaches,

    error,
  } = useSelector((state) => state.organizations);

  /**
   * =====================================================
   * LOAD ORGANIZATION
   * =====================================================
   */

  useEffect(() => {
    if (!id) return;

    dispatch(getOrganizationById(id));
  }, [dispatch, id]);

  /**
   * =====================================================
   * LOAD COACHES
   * =====================================================
   */

  useEffect(() => {
    if (!id) return;

    dispatch(
      getOrganizationCoaches({
        id,

        params: {
          page: organizationCoachesPagination?.page || 1,

          limit: organizationCoachesPagination?.limit || 12,

          search,

          status: statusFilter,
        },
      }),
    );
  }, [
    dispatch,
    id,
    search,
    statusFilter,
    organizationCoachesPagination?.page,
    organizationCoachesPagination?.limit,
  ]);

  /**
   * =====================================================
   * FILTERED
   * =====================================================
   */

  const coaches = useMemo(() => {
    return Array.isArray(organizationCoaches) ? organizationCoaches : [];
  }, [organizationCoaches]);

  /**
   * =====================================================
   * PAGINATION
   * =====================================================
   */

  const handlePageChange = (page) => {
    dispatch(
      getOrganizationCoaches({
        id,

        params: {
          page,

          limit: organizationCoachesPagination?.limit || 12,

          search,

          status: statusFilter,
        },
      }),
    );
  };

  /**
   * =====================================================
   * LOADING
   * =====================================================
   */

  if ((isLoading || isLoadingCoaches) && !coaches.length) {
    return <PageLoader title="Coaches laden..." />;
  }

  return (
    <div className="organization-coaches-page">
      {/* HEADER */}
      <PageHeader
        eyebrow="POWER"
        title={`Coaches — ${selectedOrganization?.name || ""}`}
        description="Overzicht van coaches binnen deze organisatie."
        actions={
          <button
            className="organization-coaches-page__back-button"
            onClick={() => navigate(`/dashboard/organizations/${id}`)}
          >
            <HiOutlineArrowLeft />

            <span>Terug</span>
          </button>
        }
      />

      {/* ERROR */}
      {error && <div className="organization-coaches-page__error">{error}</div>}

      {/* FILTERS */}
      <PageCard className="organization-coaches-page__filters">
        <div className="organization-coaches-page__filters-grid">
          {/* SEARCH */}
          <div className="organization-coaches-page__search">
            <HiOutlineMagnifyingGlass />

            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Zoek coach..."
              autoComplete="off"
            />
          </div>

          {/* STATUS */}
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="">Alle statussen</option>

            <option value="ACTIVE">Actief</option>

            <option value="INVITED">Uitgenodigd</option>

            <option value="BLOCKED">Geblokkeerd</option>

            <option value="DISABLED">Uitgeschakeld</option>
          </select>
        </div>
      </PageCard>

      {/* EMPTY */}
      {!isLoadingCoaches && coaches.length === 0 ? (
        <PageEmpty
          icon={<HiOutlineUserGroup />}
          title="Geen coaches gevonden"
          description="Deze organisatie bevat momenteel geen coaches."
        />
      ) : (
        <>
          {/* GRID */}
          <div className="organization-coaches-page__grid">
            {coaches.map((coach) => (
              <PageCard
                key={coach._id}
                className="organization-coaches-page__card"
              >
                {/* TOP */}
                <div className="organization-coaches-page__card-top">
                  <div>
                    <h3>
                      {coach.first_name} {coach.last_name}
                    </h3>

                    <p>{coach.email}</p>
                  </div>

                  <span
                    className={`organization-coaches-page__status organization-coaches-page__status--${String(
                      coach.status || "",
                    ).toLowerCase()}`}
                  >
                    {STATUS_LABELS[coach.status] || coach.status}
                  </span>
                </div>

                {/* META */}
                <div className="organization-coaches-page__meta">
                  {/* EMAIL */}
                  <div>
                    <strong>
                      <HiOutlineEnvelope />

                      <span>E-mail</span>
                    </strong>

                    <span>{coach.email}</span>
                  </div>

                  {/* ROLE */}
                  <div>
                    <strong>
                      <HiOutlineShieldCheck />

                      <span>Rol</span>
                    </strong>

                    <span>{ROLE_LABELS[coach.role] || coach.role}</span>
                  </div>

                  {/* LAST LOGIN */}
                  <div>
                    <strong>
                      <HiOutlineClock />

                      <span>Laatste login</span>
                    </strong>

                    <span>
                      {coach.last_login_at
                        ? new Date(coach.last_login_at).toLocaleString("nl-BE")
                        : "Nog nooit"}
                    </span>
                  </div>

                  {/* CREATED */}
                  <div>
                    <strong>
                      <HiOutlineCalendarDays />

                      <span>Aangemaakt</span>
                    </strong>

                    <span>
                      {coach.createdAt
                        ? new Date(coach.createdAt).toLocaleDateString("nl-BE")
                        : "-"}
                    </span>
                  </div>
                </div>
              </PageCard>
            ))}
          </div>

          {/* PAGINATION */}
          {organizationCoachesPagination?.pages > 1 && (
            <div className="organization-coaches-page__pagination">
              <button
                disabled={organizationCoachesPagination.page <= 1}
                onClick={() =>
                  handlePageChange(organizationCoachesPagination.page - 1)
                }
              >
                Vorige
              </button>

              <span>
                Pagina {organizationCoachesPagination.page} van{" "}
                {organizationCoachesPagination.pages}
              </span>

              <button
                disabled={
                  organizationCoachesPagination.page >=
                  organizationCoachesPagination.pages
                }
                onClick={() =>
                  handlePageChange(organizationCoachesPagination.page + 1)
                }
              >
                Volgende
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
